import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  ReactNode,
} from 'react';
import styles from './json-renderer.module.scss';
import {
  CollapseAll,
  ExpandAll,
  ChevronDown,
  ChevronRight,
  CloseOutline,
  CloseFilled,
  CheckmarkOutline,
  CheckmarkFilled,
  AddComment,
  Close,
  Edit,
  Save,
  Add,
} from '@carbon/react/icons';
import {
  Button,
  Modal,
  Tile,
  DefinitionTooltip,
  TextInput,
  TextArea,
} from '@carbon/react';
import { JSONValue, JSONObject } from 'lib/types';
import { searchJSON } from './search-json';
import { DebouncedInput } from 'components/debounced-input';
import { EmptyState } from 'components/empty-state';
import { CopyButton } from 'components/copy-button/copy-button';
import {
  IncorrectFields,
  confirmActionModalAtom,
  incorrectProvisionModalAtom,
  itemEventsAtom,
  itemMutationAtom,
  useCommentFormAtom,
  useIncorrectProvisionFormAtom,
  useValidateDetailsAtom,
  validationStateAtom,
} from './atoms';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { DropdownField } from 'components/form-fields/dropdown-field';
import { TextField } from 'components/entity-form/text-field';
import { TextAreaField } from 'components/form-fields/text-area-field';
import { getFormIsInvalidAtom, getFormValuesAtom } from 'atoms/form-atoms';
import {
  checkChildrenStatus,
  createItemsMap,
  filterNullValues,
  getPathState,
  provisionEditTarget,
  snakeToCapitalCase,
  updateValidationState,
} from './utils';
import { CompletionItemResponse } from 'types/api-responses';
import {
  HoverCard,
  HoverCardArrow,
  HoverCardContent,
  HoverCardTrigger,
} from 'components/hover-card';
import { CreateFirstComment, EventsList } from 'components/events-panel';
import { capitalizeFirstLetter } from 'utils/strings';
import { useComponentVisible } from 'lib/hooks/useComponentVisible';
import { createMutationAtom } from 'atoms/create-resource-atom';

const putLLMOutputAtom = createMutationAtom<any, 'putLLMOutput'>();
interface JSONRendererProps {
  data: JSONValue;
  completionHref: string;
  itemsHref: string;
  items: CompletionItemResponse[];
  state: JSONObject;
  documentState: JSONObject;
  refreshProvisions: () => void;
  isCompositeProvision: boolean;
  onDataUpdate: (path: string[], newValue: JSONValue) => void;
}

interface JSONNodeProps {
  data: JSONValue;
  expandState: { version: number; expanded: boolean };
  level: number;
  name?: string;
  validationState: JSONObject;
  path: string[];
  completionHref: string;
  itemsHref: string;
  refreshProvisions: () => void;
  isCompositeProvision: boolean;
  onDataUpdate: (path: string[], newValue: JSONValue) => void;
}

export function JSONRenderer({
  data,
  completionHref,
  itemsHref,
  items,
  state,
  documentState,
  refreshProvisions,
  isCompositeProvision,
  onDataUpdate,
}: JSONRendererProps) {
  const [expandState, setExpandState] = useState<{
    version: number;
    expanded: boolean;
  }>({ version: 0, expanded: false });
  const [search, setSearch] = useState<string>('');
  const [validationState, setValidationState] = useAtom(validationStateAtom);
  const [isAddProvisionModalOpen, setIsAddProvisionModalOpen] = useState(false);
  const setItemsMap = useSetAtom(itemEventsAtom);
  const nonNullData = filterNullValues(data);

  useEffect(() => {
    if (isCompositeProvision) {
      setValidationState(documentState);
    } else {
      setValidationState(state);
    }

    setItemsMap(createItemsMap(items));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExpandAll = () => {
    setExpandState({ version: Date.now(), expanded: true });
  };

  const handleCollapseAll = () => {
    setExpandState({ version: Date.now(), expanded: false });
  };

  const filteredData = searchJSON(nonNullData, search);

  return (
    <div className={styles.jsonRenderer}>
      <ConfirmActionModal />
      <IncorrectProvisionModal />
      {isCompositeProvision && (
        <AddProvisionModal
          isOpen={isAddProvisionModalOpen}
          onClose={() => setIsAddProvisionModalOpen(false)}
          completionHref={completionHref}
          refreshProvisions={refreshProvisions}
        />
      )}
      <div className={styles.actionsArea}>
        <DebouncedInput
          placeholder="Search the provision..."
          value={search}
          onChange={(value) => {
            if (typeof value === 'string') {
              if (value !== '') handleExpandAll();
              setSearch(value);
            }
          }}
        />
        <div className={styles.actionsArea}>
          {isCompositeProvision && (
            <Button
              kind="tertiary"
              hasIconOnly
              iconDescription={'Add Provision'}
              size="md"
              onClick={() => {
                setIsAddProvisionModalOpen(true);
              }}
              renderIcon={Add}
              tooltipAlignment="end"
              tooltipPosition="bottom"
            />
          )}
          <Button
            kind="tertiary"
            hasIconOnly
            iconDescription={'Collapse All'}
            size="md"
            onClick={handleCollapseAll}
            renderIcon={CollapseAll}
            tooltipAlignment="end"
            tooltipPosition="bottom"
          />
          <Button
            kind="tertiary"
            hasIconOnly
            iconDescription={'Expand All'}
            size="md"
            onClick={handleExpandAll}
            renderIcon={ExpandAll}
            tooltipAlignment="end"
            tooltipPosition="bottom"
          />
        </div>
      </div>
      <div className={styles.jsonRoot}>
        {filteredData && Object.keys(filteredData).length === 0 ? (
          <Tile>
            <EmptyState
              headerText="No Results Found"
              helperText="Try adjusting your search to find what you’re looking for."
            />
          </Tile>
        ) : (
          filteredData &&
          Object.entries(filteredData as JSONObject).map(([key, value]) => (
            <JSONNode
              key={key}
              data={value}
              expandState={expandState}
              level={0}
              name={key}
              validationState={validationState}
              path={[key]}
              completionHref={completionHref}
              itemsHref={itemsHref}
              refreshProvisions={refreshProvisions}
              isCompositeProvision={isCompositeProvision}
              onDataUpdate={onDataUpdate}
            />
          ))
        )}
      </div>
    </div>
  );
}

function AddProvisionModal({
  isOpen,
  onClose,
  completionHref,
  refreshProvisions,
}: {
  isOpen: boolean;
  onClose: () => void;
  completionHref: string;
  refreshProvisions: () => void;
}): JSX.Element {
  const [provisionName, setProvisionName] = useState('');
  const [provisionDetails, setProvisionDetails] = useState('');
  const putLLMOutput = useSetAtom(putLLMOutputAtom);

  const handleSubmit = () => {
    const { documentId, documentExtractionCompletionId } =
      provisionEditTarget(completionHref);
    const provisionKey = `user_added_provisions.${provisionName
      .toLowerCase()
      .replace(/\s+/g, '_')}`;

    putLLMOutput({
      type: 'PATCH',
      url: `/provisions/${documentId}`,
      data: {
        documentExtractionCompletionId,
        path: provisionKey,
        oldValue: null,
        newValue: provisionDetails,
      },
      onSuccess: () => {
        console.log('Successfully added new provision');
        onClose();
        refreshProvisions();
      },
      onError: (error) => {
        console.error('Error adding new provision:', error);
      },
    });
  };

  return (
    <Modal
      open={isOpen}
      onRequestClose={onClose}
      modalHeading="Add Provision"
      primaryButtonText="Add Provision"
      secondaryButtonText="Cancel"
      onRequestSubmit={handleSubmit}
    >
      <TextInput
        id="provision-name"
        labelText="Provision Name"
        value={provisionName}
        onChange={(e) => setProvisionName(e.target.value)}
        placeholder="Enter provision name"
      />
      <TextArea
        id="provision-details"
        labelText="Details"
        value={provisionDetails}
        onChange={(e) => setProvisionDetails(e.target.value)}
        placeholder="Enter provision details"
      />
    </Modal>
  );
}

const JSONNode: React.FC<JSONNodeProps> = ({
  data,
  expandState,
  level,
  name,
  validationState,
  path,
  completionHref,
  itemsHref,
  refreshProvisions,
  isCompositeProvision,
  onDataUpdate,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedValue, setEditedValue] = useState<string>('');
  const putLLMOutput = useSetAtom(putLLMOutputAtom);

  useEffect(() => {
    setIsExpanded(expandState.expanded);
  }, [expandState]);

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedValue(String(data));
  };

  const handleSave = () => {
    const { documentId, documentExtractionCompletionId } =
      provisionEditTarget(completionHref);
    const fullPath = path.join('.');

    putLLMOutput({
      type: 'PATCH',
      url: `/provisions/${documentId}`,
      data: {
        documentExtractionCompletionId,
        path: fullPath,
        oldValue: data,
        newValue: editedValue,
      },
      onSuccess: () => {
        setIsEditing(false);
        onDataUpdate(path, editedValue as JSONValue);
      },
      onError: (error) => {
        console.error('Error updating LLM output:', error);
      },
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const reviewCount = checkChildrenStatus(
    getPathState(validationState, [...path])
  );

  const renderValidationButtons = () => {
    const status = getPathState(validationState, path);

    if (typeof status === 'boolean' || status === null) {
      return (
        <StatusButtons
          status={status}
          nodeValue={data}
          path={path}
          completionHref={completionHref}
          itemsHref={itemsHref}
        />
      );
    }

    return null;
  };

  if (name?.toLowerCase() === 'id') {
    return null;
  }

  if (data === null || data === undefined) {
    return null;
  }

  // Provision-tree subtrees that own their own dedicated editor in the
  // Structured Overview (parties / non-party addresses). Editing them via
  // the generic provision-tree Edit button would bypass the structured
  // surfaces' validations and audit-event emission, so we suppress Edit and
  // the validation buttons here.
  const STRUCTURED_FIELD_ROOTS = ['parties', 'document_addresses'];
  const isInStructuredSubtree = STRUCTURED_FIELD_ROOTS.includes(path[0] ?? '');

  if (typeof data !== 'object') {
    return (
      <div className={styles.jsonNode} style={{ marginLeft: `${level * 3}px` }}>
        <span className={styles.jsonKey}>
          {name && (
            <div className={styles.actionButtons}>
              <span
                dangerouslySetInnerHTML={{ __html: snakeToCapitalCase(name) }}
              />
              <span className={styles.jsonCopybutton}>
                <CopyButton node={data} />
                <CommentButton path={path} itemsHref={itemsHref} node={name} />
                {!isInStructuredSubtree && isCompositeProvision && (
                  <Button
                    hasIconOnly
                    renderIcon={Edit}
                    iconDescription="Edit provision"
                    onClick={handleEdit}
                    size="sm"
                    kind="ghost"
                  />
                )}
                {!isInStructuredSubtree && renderValidationButtons()}
              </span>
            </div>
          )}
        </span>
        {isEditing ? (
          <div className={styles.editActions}>
            <TextInput
              id={`edit-${name}`}
              labelText={`Edit ${name}`}
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
            />
            <Button
              renderIcon={Save}
              iconDescription="Save"
              onClick={handleSave}
              size="sm"
              kind="primary"
            />
            <Button
              renderIcon={Close}
              iconDescription="Cancel"
              onClick={handleCancel}
              size="sm"
              kind="secondary"
            />
          </div>
        ) : (
          <div
            className={styles.jsonValue}
            data-path={path.join('.')}
            dangerouslySetInnerHTML={{ __html: String(data) }}
          />
        )}
      </div>
    );
  }

  const isArray = Array.isArray(data);

  return (
    <div className={styles.jsonNode} style={{ marginLeft: `${level * 3}px` }}>
      <div onClick={toggleExpand} className={styles.jsonExpandable}>
        <div className={styles.jsonNodeTitle}>
          <span className={styles.jsonArrow}>
            {isExpanded ? <ChevronDown /> : <ChevronRight />}
          </span>
          {name && (
            <span
              className={styles.jsonKey}
              dangerouslySetInnerHTML={{ __html: snakeToCapitalCase(name) }}
            />
          )}
        </div>
        <div>
          {reviewCount && <EventsCount reviewCount={reviewCount} path={path} />}
        </div>
      </div>
      {isExpanded && (
        <div className={styles.jsonChildren}>
          {Object.entries(data as JSONObject).map(([key, value], index) => (
            <JSONNode
              key={key}
              data={value}
              expandState={expandState}
              level={level + 1}
              name={isArray ? `${index + 1}` : key}
              validationState={validationState}
              path={[...path, key]}
              completionHref={completionHref}
              itemsHref={itemsHref}
              refreshProvisions={refreshProvisions}
              isCompositeProvision={isCompositeProvision}
              onDataUpdate={onDataUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function EventsCount({
  path,
  reviewCount,
}: {
  path: string[];
  reviewCount: { correct: number; incorrect: number };
}) {
  const itemEvents = useAtomValue(itemEventsAtom);
  const joinedPath = path.join('.');
  let total = 0;
  for (const itemEventKey of Object.keys(itemEvents)) {
    if (itemEventKey.includes(joinedPath)) {
      const event = itemEvents[itemEventKey];
      total += event ? event.commentsCount : 0;
    }
  }
  return (
    <div className={styles.eventsCount}>
      {total > 0 && (
        <>
          <AddComment /> {total}
        </>
      )}
      {reviewCount.correct > 0 && (
        <>
          <CheckmarkFilled /> {reviewCount.correct}
        </>
      )}
      {reviewCount.incorrect > 0 && (
        <>
          <CloseFilled /> {reviewCount.incorrect}
        </>
      )}
    </div>
  );
}

function StatusButtons({
  status,
  nodeValue,
  path,
  itemsHref,
}: {
  status: boolean | null;
  nodeValue: JSONValue;
  path: string[];
  completionHref: string;
  itemsHref: string;
}) {
  const [, setItemEvents] = useAtom(itemEventsAtom);
  const setConfirmationModal = useSetAtom(confirmActionModalAtom);
  const setIncorrectProvisionModal = useSetAtom(incorrectProvisionModalAtom);
  const [validationState, setValidationState] = useAtom(validationStateAtom);
  const [completionItem, completionItemMutation] = useAtom(itemMutationAtom);
  const node = path.slice(-1).pop();
  const terminalNode = snakeToCapitalCase(node || '');
  const terminalNodeValue = nodeValue;
  const joinedPath = path.join('.');

  const onChange = (
    status: boolean | null,
    incorrectFields?: IncorrectFields
  ) => {
    const newState = updateValidationState(path, validationState, status);
    completionItemMutation({
      url: itemsHref,
      type: 'POST',
      data: {
        path: path.join('.'),
        provision: {
          value: nodeValue,
        },
        comment: incorrectFields?.comment
          ? { content: incorrectFields.comment }
          : undefined,
        event: {
          type:
            status === null
              ? null
              : status
                ? 'completion_correct'
                : 'completion_incorrect',
          reason: incorrectFields?.reason,
          details: incorrectFields?.details,
        },
      },
      onSuccess: (item) => {
        setValidationState(newState);
        setItemEvents((current) => ({
          ...current,
          [joinedPath]: item,
        }));
      },
    });
  };
  return (
    <div
      className={
        status === false || status === true
          ? styles.statusButtonsOn
          : styles.statusButtons
      }
    >
      <Button
        size="sm"
        hasIconOnly
        kind={'ghost'}
        iconDescription={
          status === false ? 'Remove Incorrect Status' : 'Mark as Incorrect'
        }
        tooltipAlignment="center"
        tooltipPosition="top"
        renderIcon={status === false ? CloseFilled : CloseOutline}
        className={status === false ? styles.notApprovedBtn : ''}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          if (status === false) {
            setConfirmationModal({
              currentState: 'incorrect',
              terminalNode,
              onConfirmAction: (comment) => {
                onChange(null, { comment: comment });
              },
            });
          }

          if (status === true || status === null) {
            setIncorrectProvisionModal({
              terminalNode,
              terminalNodeValue,
              onIncorrect: (form: IncorrectFields) => {
                onChange(false, form);
              },
            });
          }
        }}
        disabled={completionItem.loading}
      />
      <Button
        size="sm"
        hasIconOnly
        kind={'ghost'}
        iconDescription={
          status === true ? 'Remove Correct Status' : 'Mark as Correct'
        }
        tooltipAlignment="center"
        tooltipPosition="top"
        renderIcon={status === true ? CheckmarkFilled : CheckmarkOutline}
        className={status === true ? styles.approvedBtn : ''}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();

          if (status === false) {
            setConfirmationModal({
              currentState: 'incorrect',
              terminalNode: snakeToCapitalCase(node || ''),
              onConfirmAction: () => {
                onChange(true);
              },
            });
          }

          if (status === true) {
            setConfirmationModal({
              currentState: 'correct',
              terminalNode: snakeToCapitalCase(node || ''),
              onConfirmAction: () => {
                onChange(null);
              },
            });
          }

          if (status === null) {
            onChange(true);
          }
        }}
        disabled={completionItem.loading}
      />
    </div>
  );
}

function CommentButton({
  path,
  itemsHref,
  node,
}: {
  path: string[];
  itemsHref: string;
  node: string;
}) {
  const [itemEvents, setItemEvents] = useAtom(itemEventsAtom);
  const [completionItem, completionItemMutation] = useAtom(itemMutationAtom);
  const joinedPath = path.join('.');
  const itemEvent = itemEvents[joinedPath];
  const title = 'No comments added';
  const helperText = 'Add a comment for yourself or leave a note for others.';
  const { ref, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  return (
    <div ref={ref}>
      <HoverCard open>
        {isComponentVisible && (
          <HoverCardContent
            align="center"
            side="bottom"
            className={styles.commentsHoverCard}
          >
            <HoverCardArrow className={styles.hoverCardArrow} />
            <div>
              <div className={styles.closeButtonArea}>
                <span
                  dangerouslySetInnerHTML={{ __html: snakeToCapitalCase(node) }}
                />
                <Button
                  kind="ghost"
                  hasIconOnly
                  iconDescription={'Close'}
                  size="md"
                  onClick={() => setIsComponentVisible((current) => !current)}
                  renderIcon={Close}
                  className={styles.closeButton}
                  tooltipAlignment="end"
                  tooltipPosition="left"
                />
              </div>

              {itemEvent ? (
                <EventsList
                  title={title}
                  helperText={helperText}
                  eventsHref={itemEvent.resources.events.href}
                  commentsHref={itemEvent.resources.comments.href}
                  commentsPanel
                  onComment={() => {
                    setItemEvents((current) => ({
                      ...current,
                      [joinedPath]: {
                        ...itemEvent,
                        commentsCount: ++itemEvent.commentsCount,
                      },
                    }));
                  }}
                />
              ) : (
                <CreateFirstComment
                  saving={completionItem.loading}
                  title={title}
                  helperText={helperText}
                  onCreateComment={(comment) => {
                    completionItemMutation({
                      url: itemsHref,
                      type: 'POST',
                      data: {
                        path: joinedPath,
                        comment: { content: comment },
                      },
                      onSuccess: (item) => {
                        setItemEvents((current) => ({
                          ...current,
                          [joinedPath]: item,
                        }));
                      },
                    });
                  }}
                />
              )}
            </div>
          </HoverCardContent>
        )}
        <HoverCardTrigger asChild>
          <span
            className={itemEvent ? styles.activeComments : undefined}
            onClick={() => setIsComponentVisible((current) => !current)}
          >
            <DefinitionTooltip
              align="top"
              definition="Add Comment"
              className={styles.commentTooltip}
            >
              <AddComment />

              {itemEvent && itemEvent.commentsCount > 0
                ? itemEvent.commentsCount
                : ''}
            </DefinitionTooltip>
          </span>
        </HoverCardTrigger>
      </HoverCard>
    </div>
  );
}

function ConfirmActionModal() {
  const [confirmAction, setConfirmAction] = useAtom(confirmActionModalAtom);
  const confirmActionMessage = capitalizeFirstLetter(
    confirmAction?.currentState || ''
  );
  const formAtom = useCommentFormAtom(!!confirmAction);
  const form = useAtomValue(formAtom);
  const formValuesAtom = useMemo(() => getFormValuesAtom(formAtom), [formAtom]);
  const formValues = useAtomValue(formValuesAtom);

  return (
    <Modal
      size="xs"
      modalHeading={`Remove ${confirmActionMessage} Status?`}
      danger
      secondaryButtonText="Cancel"
      primaryButtonText="Remove"
      className={styles.confirmActionModal}
      open={!!confirmAction}
      onRequestClose={() => setConfirmAction(undefined)}
      onRequestSubmit={() => {
        if (confirmAction) {
          confirmAction.onConfirmAction(
            formValues.comment ? formValues.comment : undefined
          );
          setConfirmAction(undefined);
        }
      }}
    >
      {confirmAction && (
        <>
          <p>
            This will remove the {confirmActionMessage} status for &quot;
            {capitalizeFirstLetter(confirmAction.terminalNode)}&quot;.
          </p>
          {confirmAction.currentState === 'incorrect' && (
            <fieldset>
              <TextAreaField
                id="incorrect-comment"
                labelText="Add Comment (optional)"
                fieldAtom={form.comment}
              />
            </fieldset>
          )}
        </>
      )}
    </Modal>
  );
}

function IncorrectProvisionModal() {
  const [incorrectProvision, setIncorrectProvision] = useAtom(
    incorrectProvisionModalAtom
  );
  const formAtom = useIncorrectProvisionFormAtom(!!incorrectProvision);
  const form = useAtomValue(formAtom);
  const invalidFormAtom = useMemo(
    () => getFormIsInvalidAtom(formAtom),
    [formAtom]
  );
  const formValuesAtom = useMemo(() => getFormValuesAtom(formAtom), [formAtom]);
  const reason = useAtomValue(form.reason);
  const setValidation = useValidateDetailsAtom(form.reason, form.details);
  const formInvalid = useAtomValue(invalidFormAtom);
  const formValues = useAtomValue(formValuesAtom);

  useEffect(() => {
    setValidation();
  }, [reason, setValidation]);

  return (
    <Modal
      danger
      size="sm"
      className={styles.markIncorrectModal}
      secondaryButtonText="Cancel"
      primaryButtonText="Mark as Incorrect"
      primaryButtonDisabled={formInvalid}
      open={!!incorrectProvision}
      modalHeading="Mark as Incorrect"
      onRequestClose={() => setIncorrectProvision(undefined)}
      onRequestSubmit={() => {
        if (incorrectProvision) {
          incorrectProvision.onIncorrect(formValues);
          setIncorrectProvision(undefined);
        }
      }}
    >
      {incorrectProvision && (
        <div>
          <h4>{incorrectProvision.terminalNode}</h4>
          <p>{incorrectProvision.terminalNodeValue as ReactNode}</p>
          <fieldset>
            <DropdownField
              titleText="Select Reason "
              required
              field={form.reason}
              label="Choose an option"
              itemToString={(item: string) => item}
              size="md"
              id="incorrect-reason"
              items={[
                '',
                'Wrong Legal Description',
                'Typo/Misspelling',
                'Wrong Date',
                'Wrong Name',
                'Wrong Page',
                'Other',
              ]}
            />
          </fieldset>
          <fieldset>
            {reason.value === 'Other' && (
              <TextField
                id="reason-details"
                fieldAtom={form.details}
                labelText="Details"
                required
              />
            )}
          </fieldset>
          <fieldset>
            <TextAreaField
              id="incorrect-comment"
              labelText="Add Comment (optional)"
              fieldAtom={form.comment}
            />
          </fieldset>
        </div>
      )}
    </Modal>
  );
}
